// Canonical discount-code classification logic.
//
// This used to be duplicated (the "100% + forever = lifetime" check)
// across create-checkout, stripe-webhook, check-subscription and
// UpgradePage. Centralise it here so web + edge functions agree on what
// a code means. The edge-function runtime can't import from
// packages/shared (Deno, URL imports), so a byte-identical copy lives at
// supabase/functions/_shared/discounts.ts — keep the two in sync.

export type DiscountDurationType = "once" | "repeating" | "forever";

/**
 * The subset of a discount_codes row that classification depends on.
 * discount_value may arrive as a string (Postgres numeric → PostgREST
 * serialises it as a string to preserve precision), so everything that
 * touches it coerces with Number().
 */
export interface DiscountLike {
  discount_type?: string | null;
  discount_value?: number | string | null;
  duration_type?: string | null;
  duration_months?: number | null;
  grants_free_access?: boolean | null;
}

/** 100%-off forever = permanent free access (plan_type "lifetime_free"). */
export function isLifetimeFree(d: DiscountLike): boolean {
  return (
    d.discount_type === "percentage" &&
    Number(d.discount_value) === 100 &&
    d.duration_type === "forever"
  );
}

/**
 * once/repeating durations are measured in months. A Stripe "once"
 * coupon discounts the FIRST invoice — which on a yearly plan is a whole
 * year — so a month-scoped code must never be applied to yearly billing.
 */
export function isMonthScopedDuration(durationType?: string | null): boolean {
  return durationType === "once" || durationType === "repeating";
}

/**
 * How many months a duration covers: once = 1, repeating = duration_months
 * (at least 1), forever = null (unbounded).
 */
export function durationInMonths(d: DiscountLike): number | null {
  if (d.duration_type === "once") return 1;
  if (d.duration_type === "repeating") {
    return Math.max(1, Math.floor(Number(d.duration_months) || 1));
  }
  return null; // forever / unknown
}

/**
 * A "free access" (comp) code grants full access for a fixed period with
 * NO payment method and NO auto-charge afterwards. Distinct from an
 * ordinary discount, which sets up a renewing paid Stripe subscription.
 */
export function isFreeAccessCode(d: DiscountLike): boolean {
  return d.grants_free_access === true;
}

/**
 * End of a free-access grant counted from `start`. forever → null (never
 * expires). Month arithmetic is calendar-based (1 month = same day next
 * month).
 */
export function computeAccessEndDate(start: Date, d: DiscountLike): string | null {
  const months = durationInMonths(d);
  if (months === null) return null;
  const end = new Date(start.getTime());
  // UTC-based so a DST change between start and end can't shift the time
  // (edge functions run in UTC; the web may not).
  end.setUTCMonth(end.getUTCMonth() + months);
  return end.toISOString();
}

/**
 * Whether applying this code to the chosen billing interval is allowed.
 * Month-scoped (once/repeating) codes on a yearly plan would cover/discount
 * a full year — never the intent — so they're rejected. forever codes and
 * free-access codes (which bypass Stripe billing entirely) are unaffected.
 */
export function isApplicableToBillingInterval(
  d: DiscountLike,
  billingInterval: "monthly" | "yearly",
): boolean {
  if (isFreeAccessCode(d)) return true;
  if (billingInterval === "yearly" && isMonthScopedDuration(d.duration_type)) {
    return false;
  }
  return true;
}

/** Human-readable reason a code can't be applied to an interval (UI + API copy). */
export function billingIntervalRejectionMessage(d: DiscountLike): string {
  const months = durationInMonths(d);
  const period = months === 1 ? "1 month" : `${months} months`;
  return `This code is valid for ${period} and can only be used on a monthly plan, not a yearly plan.`;
}
